/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useMemo, useState } from "react";
import { apiClient } from "../api";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle
} from "./ui/card";

interface RawNewsItem {
  [key: string]: any;
}

interface NormalizedNewsItem {
  title: string;
  summary?: string;
  snippet?: string;
  href?: string;
  source?: string;
  published?: string;
  notes?: string;
}

interface DailyNewsApiResponse {
  publishDate?: string;
  PublishDate?: string;
  payload?: any;
  Payload?: any;
}

interface DailyNewsPanelProps {
  showHeading?: boolean;
}

const bannedDomains = ["wikipedia.org", "fandom.com"];

const isLikelyUrl = (value?: string | null) => {
  return typeof value === "string" && /^https?:\/\//i.test(value.trim());
};

const pickFirstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      const trimmed = value.trim();
      if (trimmed.length > 0) {
        return trimmed;
      }
    }
  }
  return undefined;
};

const normalizeItem = (item: RawNewsItem): NormalizedNewsItem => {
  if (!item) {
    return { title: "" };
  }

  const href = pickFirstString(
    item.url,
    item.Url,
    item.URL,
    item.link,
    item.Link,
    item.href,
    item.Href,
    item.permalink,
    item.permalinkUrl,
    item.permalink_url,
    item.permaLink,
    item.sourceUrl,
    item.source_url,
    item.sourceURL,
    item.webUrl,
    item.web_url,
    item.WebUrl,
    item.WebURL,
    item.website,
    item.siteUrl,
    item.site_url
  );

  const title =
    pickFirstString(
      item.title,
      item.Title,
      item.headline,
      item.Headline,
      item.name,
      item.Name,
      item.text,
      item.Text
    ) ?? href ?? "";

  const summary = pickFirstString(
    item.summary,
    item.Summary,
    item.description,
    item.Description,
    item.content,
    item.Content,
    item.blurb
  );

  const snippet = pickFirstString(
    item.snippet,
    item.Snippet,
    item.snippetText,
    item.SnippetText,
    item.snippet_text,
    item.preview,
    item.Preview,
    item.excerpt,
    item.Excerpt
  );

  const sourceLabel = pickFirstString(
    item.source,
    item.Source,
    item.sourceName,
    item.source_name,
    item.publisher,
    item.publisherName,
    item.publisher_name,
    item.outlet,
    item.domain,
    item.site,
    item.origin
  );

  const published = pickFirstString(
    item.published,
    item.Published,
    item.publishedAt,
    item.PublishedAt,
    item.date,
    item.Date,
    item.lastUpdated,
    item.last_updated
  );

  const notes = pickFirstString(item.notes, item.Notes);

  const resolvedTitle = title || sourceLabel || href || "";

  return {
    title: resolvedTitle,
    summary,
    snippet,
    href,
    source: sourceLabel ?? (href ? extractHost(href) : undefined),
    published,
    notes
  };
};

const extractItems = (payload: any, visited = new Set<any>()): NormalizedNewsItem[] => {
  if (!payload) {
    return [];
  }

  const tryParseJson = (value: any) => {
    if (typeof value === "string") {
      try {
        return JSON.parse(value);
      } catch {
        return null;
      }
    }
    return value;
  };

  const parsed = tryParseJson(payload) ?? payload;

  if (typeof parsed === "object" && parsed !== null) {
    if (visited.has(parsed)) {
      return [];
    }
    visited.add(parsed);
  }

  const normalizedItems: NormalizedNewsItem[] = [];

  if (Array.isArray(parsed?.citations)) {
    const citationItems = parsed.citations
      .map((citation: any) => {
        if (typeof citation === "string") {
          return normalizeItem({ title: citation, source: citation });
        }
        return normalizeItem(citation);
      })
      .filter(item => item.title && item.title.trim().length > 0);

    normalizedItems.push(...citationItems);
  }

  const candidateLists: any[] = [
    Array.isArray(parsed) ? parsed : null,
    Array.isArray(parsed?.items) ? parsed.items : null,
    Array.isArray(parsed?.item) ? parsed.item : null,
    Array.isArray(parsed?.articles) ? parsed.articles : null,
    Array.isArray(parsed?.article) ? parsed.article : null,
    Array.isArray(parsed?.search_results) ? parsed.search_results : null,
    Array.isArray(parsed?.searchResults) ? parsed.searchResults : null,
    Array.isArray(parsed?.results) ? parsed.results : null,
    Array.isArray(parsed?.news) ? parsed.news : null,
    Array.isArray(parsed?.newsItems) ? parsed.newsItems : null,
    Array.isArray(parsed?.entries) ? parsed.entries : null,
    Array.isArray(parsed?.stories) ? parsed.stories : null,
    Array.isArray(parsed?.documents) ? parsed.documents : null
  ].filter(Array.isArray);

  if (candidateLists.length > 0) {
    normalizedItems.push(
      ...candidateLists
        .flat()
        .map(candidate =>
          typeof candidate === "string"
            ? normalizeItem({ title: candidate, source: candidate })
            : normalizeItem(candidate)
        )
        .filter(item => item.title && item.title.trim().length > 0)
    );
  }

  const nestedCandidates = [
    parsed?.payload,
    parsed?.Payload,
    parsed?.payloadJson,
    parsed?.PayloadJson,
    parsed?.payloadJSON,
    parsed?.data,
    parsed?.Data,
    parsed?.result,
    parsed?.Result,
    parsed?.response,
    parsed?.Response
  ];

  for (const nested of nestedCandidates) {
    if (nested && nested !== parsed) {
      normalizedItems.push(...extractItems(nested, visited));
    }
  }

  const mergeText = (current?: string, incoming?: string) => {
    const currentTrimmed = current?.trim();
    const incomingTrimmed = incoming?.trim();
    if (!currentTrimmed) {
      return incomingTrimmed;
    }
    if (!incomingTrimmed) {
      return currentTrimmed;
    }
    if (isLikelyUrl(currentTrimmed) && !isLikelyUrl(incomingTrimmed)) {
      return incomingTrimmed;
    }
    if (!isLikelyUrl(currentTrimmed) && isLikelyUrl(incomingTrimmed)) {
      return currentTrimmed;
    }
    return incomingTrimmed.length > currentTrimmed.length ? incomingTrimmed : currentTrimmed;
  };

  const mergeItems = (base: NormalizedNewsItem, candidate: NormalizedNewsItem): NormalizedNewsItem => {
    const mergedHref = base.href || candidate.href;
    const merged: NormalizedNewsItem = {
      title: mergeText(base.title, candidate.title) ?? base.title ?? candidate.title ?? "",
      summary: mergeText(base.summary, candidate.summary),
      snippet: mergeText(base.snippet, candidate.snippet),
      href: mergedHref,
      source: mergeText(base.source, candidate.source),
      published: mergeText(base.published, candidate.published),
      notes: mergeText(base.notes, candidate.notes)
    };

    if ((!merged.source || merged.source.trim().length === 0) && mergedHref) {
      merged.source = extractHost(mergedHref);
    }

    return merged;
  };

  const deduped = new Map<string, NormalizedNewsItem>();

  for (const item of normalizedItems) {
    const titleKey = item.title?.trim().toLowerCase() ?? "";
    const hrefKey = item.href?.trim().toLowerCase();
    const key = hrefKey && hrefKey.length > 0 ? hrefKey : titleKey;

    if (!key) {
      continue;
    }

    const existing = deduped.get(key);
    if (!existing) {
      deduped.set(key, item);
    } else {
      deduped.set(key, mergeItems(existing, item));
    }
  }

  return Array.from(deduped.values());
};

const filterNews = (items: NormalizedNewsItem[]): NormalizedNewsItem[] => {
  return items.filter(item => {
    const hrefCandidate =
      item.href ??
      (typeof item.source === "string" && item.source.startsWith("http") ? item.source : undefined);
    const domainTarget = hrefCandidate ?? item.source ?? "";

    if (!domainTarget) {
      return false;
    }

    const lowerTarget = domainTarget.toLowerCase();

    if (lowerTarget.endsWith(".pdf")) {
      return false;
    }

    const host = extractHost(domainTarget).toLowerCase();

    if (!host) {
      return true;
    }

    return !bannedDomains.some(domain => host.includes(domain));
  });
};

const formatDate = (value?: string) => {
  if (!value) {
    return "";
  }

  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  } catch {
    return value;
  }
};

function extractHost(value?: string) {
  if (!value) {
    return "";
  }

  try {
    const url = new URL(value);
    return url.hostname.replace(/^www\./i, "");
  } catch {
    return value;
  }
}

const DailyNewsPanel: React.FC<DailyNewsPanelProps> = ({ showHeading = true }) => {
  const [newsItems, setNewsItems] = useState<NormalizedNewsItem[] | null>(null);
  const [publishDate, setPublishDate] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const fetchNews = async () => {
      try {
        const response = await apiClient.get("/api/news", {
          validateStatus: status => status >= 200 && status < 500
        });

        if (!isMounted) {
          return;
        }

        if (response.status === 204 || !response.data) {
          setNewsItems([]);
          setPublishDate(null);
          setIsLoading(false);
          return;
        }

        if (response.status >= 400) {
          throw new Error(`Request failed with status ${response.status}`);
        }

        const data = response.data as DailyNewsApiResponse & {
          payloadJson?: any;
          PayloadJson?: any;
          payloadJSON?: any;
        };
        const rawPayload =
          data.payload ??
          data.Payload ??
          data.payloadJson ??
          data.PayloadJson ??
          data.payloadJSON;
        const extractedRaw = extractItems(rawPayload);
        console.debug("DailyNewsPanel fetched payload", { data, rawPayload, extractedRaw });
        const extracted = filterNews(extractedRaw);
        console.debug("DailyNewsPanel filtered items", { extracted });

        setNewsItems(extracted);
        setPublishDate((data.publishDate ?? data.PublishDate) ?? null);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unexpected error fetching news.");
        setNewsItems(null);
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchNews();

    return () => {
      isMounted = false;
    };
  }, []);

  const displayPublishDate = useMemo(() => {
    return publishDate ? formatDate(publishDate) : "";
  }, [publishDate]);

  return (
    <div className="space-y-4 text-sm">
      {showHeading && <h2 className="text-lg font-semibold">Daily Guitar News</h2>}
      {displayPublishDate && (
        <p className="text-xs text-muted-foreground">Curated for {displayPublishDate}</p>
      )}

      {isLoading && <p className="text-muted-foreground">Loading the latest guitar headlines...</p>}

      {!isLoading && error && <p className="text-destructive">{error}</p>}

      {!isLoading && !error && newsItems !== null && newsItems.length === 0 && (
        <p className="text-muted-foreground">No fresh guitar news yet? Check back soon!</p>
      )}

      {!isLoading && !error && newsItems && newsItems.length > 0 && (
        <div className="grid gap-4">
          {newsItems.map((item, index) => {
            const snippetText = item.snippet?.trim();
            const summaryText = item.summary?.trim();
            const notesText = item.notes?.trim();
            const supportingSummary =
              summaryText && snippetText && summaryText !== snippetText
                ? summaryText
                : !snippetText
                  ? summaryText
                  : undefined;
            const href =
              item.href ??
              (typeof item.source === "string" && item.source.startsWith("http")
                ? item.source
                : undefined);
            const sourceLabel = item.source?.trim();
            const hostLabel = extractHost(href ?? sourceLabel);
            const publishedLabel = item.published ? formatDate(item.published) : "";
            const rawTitle = item.title?.trim();
            const displayTitle = (() => {
              if (rawTitle && !isLikelyUrl(rawTitle)) {
                return rawTitle;
              }

              const urlTarget = href ?? rawTitle;
              if (urlTarget && isLikelyUrl(urlTarget)) {
                try {
                  const parsed = new URL(urlTarget);
                  const path =
                    parsed.pathname && parsed.pathname !== "/"
                      ? `${parsed.hostname}${parsed.pathname}`
                      : parsed.hostname;
                  return decodeURIComponent(path);
                } catch {
                  return urlTarget;
                }
              }

              return hostLabel || sourceLabel || "Untitled news item";
            })();
            const resolvedSourceLabel = (() => {
              if (href && hostLabel) {
                return hostLabel;
              }
              if (sourceLabel) {
                if (isLikelyUrl(sourceLabel)) {
                  return extractHost(sourceLabel);
                }
                const lowered = sourceLabel.toLowerCase();
                if (["web", "news", "site", "source"].includes(lowered)) {
                  return hostLabel;
                }
                return sourceLabel;
              }
              return hostLabel;
            })();
            const hasBody = Boolean(snippetText || supportingSummary || notesText);

            return (
              <Card
                key={`${item.title}-${index}`}
                className="transition-shadow duration-200 hover:shadow-md"
              >
                <CardHeader className="space-y-2 pb-4">
                  <CardTitle className="text-base leading-tight">
                    {href ? (
                      <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary underline-offset-4 hover:underline"
                      >
                        {displayTitle}
                      </a>
                    ) : (
                      displayTitle
                    )}
                  </CardTitle>
                  {(resolvedSourceLabel || publishedLabel) && (
                    <CardDescription className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      {resolvedSourceLabel
                        ? href
                          ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="font-medium text-primary underline-offset-4 hover:underline"
                            >
                              {resolvedSourceLabel}
                            </a>
                            )
                          : (
                            <span className="font-medium text-muted-foreground">{resolvedSourceLabel}</span>
                            )
                        : null}
                      {publishedLabel && (
                        <span className="text-muted-foreground">
                          {resolvedSourceLabel ? "| " : ""}
                          {`Published ${publishedLabel}`}
                        </span>
                      )}
                    </CardDescription>
                  )}
                </CardHeader>
                {hasBody && (
                  <CardContent className="space-y-3 text-sm leading-relaxed">
                    {snippetText && (
                      <blockquote className="rounded-md border border-primary/15 bg-primary/5 px-4 py-3 italic text-muted-foreground">
                        {snippetText}
                      </blockquote>
                    )}
                    {supportingSummary && <p>{supportingSummary}</p>}
                    {notesText && (
                      <p className="text-xs text-muted-foreground">
                        <span className="font-semibold text-foreground">Notes:</span> {notesText}
                      </p>
                    )}
                  </CardContent>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DailyNewsPanel;






